import { ApolloClient, gql, isApolloError, NormalizedCacheObject, QueryOptions } from '@apollo/client'
import { getCookie } from 'cookies-next'
import { ServerResponse } from 'http'
import { AUTH_JWT_COOKIE } from '@/constants/cookies'
import type { User } from '@/services/UserService'
import { GET_USER_PROFILE_QUERY } from '@/services/UserService/queries'
import { GuildPlan, UserProfileQuery } from '@/types/codegen-federation'
import { decodeAccessToken } from '../auth-utils'
import { logger } from '../logging'
import { getStringOrEmptyString, getValidString } from '../primitives/strings'
import { Maybe, ServerRequest } from '../types'

export function getAccessTokenFromServer(req: ServerRequest, res?: ServerResponse): string {
  return getStringOrEmptyString(getValidString(getCookie(AUTH_JWT_COOKIE, { req, res })))
}

export function getUserUuidFromJwt(req: ServerRequest, res?: ServerResponse): string | undefined {
  const accessToken = getAccessTokenFromServer(req, res)
  return decodeAccessToken(accessToken)?.userId
}

export function getUserIdAndAccessToken(
  req: ServerRequest,
  res?: ServerResponse,
): { userId?: string; accessToken?: string } {
  const accessToken = getValidString(getCookie(AUTH_JWT_COOKIE, { req, res }))
  if (!accessToken) return {}
  const decoded = decodeAccessToken(accessToken)
  return { userId: decoded?.userId, accessToken }
}

export async function getUser(
  client: ApolloClient<object>,
  userId: Maybe<string>,
  options?: QueryOptions,
): Promise<User | null> {
  if (!userId) {
    return null
  }

  try {
    const result = await client.query<UserProfileQuery>({
      query: GET_USER_PROFILE_QUERY,
      fetchPolicy: 'no-cache',
      errorPolicy: 'none',
      ...options,
    })
    if (result.errors?.length) {
      logger().error('Failed to fetch user profile', { errors: result.errors })
      return null
    }
    return result.data?.user ?? null
  } catch (e) {
    const err = e as Error
    if (isApolloError(err)) {
      if (err.graphQLErrors.length && err.graphQLErrors.some((e) => e.message === 'Not Authorized')) {
        logger().warn(`User not authorized. Auth token is either missing or invalid`, { err })
        return null
      }
    }
    logger().error('Failed to fetch user profile', { err })
    return null
  }
}

export async function getUserGuildStatus(uuid: Maybe<string>): Promise<GuildStatus | null> {
  if (!uuid) return null

  if (typeof window !== 'undefined') {
    throw new Error('getUserGuildStatus should only be called on the server!')
  }

  if (!process.env.GUILD_NFT_URL || !process.env.GUILD_NFT_API_KEY) {
    throw new Error('Missing GUILD_NFT_URL or GUILD_NFT_API_KEY environment variables!')
  }

  const baseUrl = `${process.env.GUILD_NFT_URL}/guildUser/${uuid}`

  try {
    const response = await fetch(baseUrl, {
      method: 'GET',
      headers: {
        Authorization: process.env.GUILD_NFT_API_KEY,
      },
    })

    if (!response.ok) {
      return null
    }

    const data: GuildStatus = await response.json()
    return data
  } catch (error) {
    logger().error('Failed to fetch guild user status', { error })
    return null
  }
}

/**
 * The GuildStatus interface is used through the app, especially on the server, to
 * identify the roles and permissions of a user relating to guild features.
 * Previously, isGuildMember and guildRoles were requested from the Token Service
 * /guildUser/:uuid endpoint. Now, they are requested from fed graph user type and
 * set in implementers of this interface.
 */
export interface GuildStatus {
  isGuildMember: boolean
  guildMemberStart: Date | null
  guildMemberEnd: Date | null
  guildMemberReason: string
  guildPlan: GuildPlan
  paymentPlatform?: string | null
  guildRoles: {
    hasEarlyAccess: boolean
    hasFreeTickets: boolean
    hasMerchDiscount: boolean
  }
}

export const GET_USER_PUBLIC_PROFILE = gql`
  query GetUser($uuid: ID!, $fallbackToInitials: Boolean) {
    publicUserProfile(uuid: $uuid) {
      firstName
      lastName
      image(fallbackToInitials: $fallbackToInitials)
      id
      city
      state
      country
    }
  }
`

export const getUserPublicProfile = async (
  client: ApolloClient<NormalizedCacheObject>,
  fallbackToInitials = true,
  uuid?: string,
) => {
  const { data } = await client.query({
    query: GET_USER_PUBLIC_PROFILE,
    variables: { uuid, fallbackToInitials },
    errorPolicy: 'all',
  })
  return data.publicUserProfile
}

export interface UserPublicProfile {
  firstName: string
  lastName: string
  image: string
  id: string
  city?: string
  state?: string
  country?: string
}
